/**
 * BuildStamp
 * ----------
 * Tiny lower-right corner overlay that surfaces *which build is running*.
 *
 * Why this exists
 * ---------------
 * Without an on-page build identifier it is essentially impossible to tell
 * whether a CloudFront cache, an old browser tab, or a stale S3 object is
 * the reason a user is "still seeing the old behavior" after a deploy.
 * P3-S5 (PR #27) closed an incident in which the wrong .env got built into
 * the bundle; debugging that took hours partly because there was no way
 * to confirm from the live site which commit was actually serving.
 *
 * Source of truth
 * ---------------
 *   process.env.REACT_APP_GIT_SHA     — the merge commit SHA, injected at
 *                                       build time by .github/workflows/
 *                                       deploy-client.yml's Build step
 *                                       (`github.sha`).
 *   process.env.REACT_APP_BUILD_TIME  — ISO 8601 build timestamp, also
 *                                       injected by the workflow
 *                                       (`github.run_started_at`).
 *
 * Both are baked into the JS bundle at `npm run build` time. They cannot
 * change at runtime, which is exactly the contract we want — the stamp
 * identifies *the bundle*, not the page-load.
 *
 * Local development
 * -----------------
 * In `npm start` (no CI env injection) both vars are undefined; we render
 * "build dev · <local-time>" with no commit link. This makes the component
 * safe to mount unconditionally in App.jsx without dev/prod branching.
 *
 * Click-through
 * -------------
 * In production (when GIT_SHA is set), the stamp is an `<a>` linking to
 * the GitHub commit page. In dev (`'dev'`) it renders as a static span.
 * The repo URL is hardcoded — this is operator/diagnostic UI, not a
 * configurable user-facing feature, so a constant is appropriate.
 *
 * Bundle-leak-gate note
 * ---------------------
 * This file references `process.env.REACT_APP_GIT_SHA` and
 * `REACT_APP_BUILD_TIME`. The bundle leak gate in deploy-client.yml
 * scans for the `REACT_APP_AWS_*_KEY` patterns (the originals from the
 * PR #7 / P3-S5 incident) — different env-var names, no false-positive
 * risk on this file. NOTE: the gate scans both *.js AND *.js.map; source
 * maps preserve original source comments, so any explanatory comment
 * MUST avoid the literal trigger pattern. We use the wildcard form
 * `REACT_APP_AWS_*_KEY` here precisely to dodge that self-trip — see
 * runbook Session 6 → "Operator-friendly note: bundle leak gate
 * self-trip on comments" (added in PR #29) for the precedent.
 */
import React from 'react';

const REPO_URL = 'https://github.com/thodkin/authprod';

const SHA = process.env.REACT_APP_GIT_SHA || 'dev';
const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || new Date().toISOString();

const SHORT_SHA = SHA === 'dev' ? 'dev' : SHA.slice(0, 7);

// Format BUILD_TIME for display in the user's locale. We keep the ISO
// string in the title attribute so operators can still copy the canonical
// UTC timestamp on hover.
const FRIENDLY_TIME = (() => {
  try {
    const d = new Date(BUILD_TIME);
    if (Number.isNaN(d.getTime())) return BUILD_TIME;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch (_e) {
    return BUILD_TIME;
  }
})();

const STAMP_STYLE = {
  position: 'fixed',
  bottom: '4px',
  right: '8px',
  fontSize: '10px',
  lineHeight: '1.2',
  color: 'rgba(0, 0, 0, 0.45)',
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  zIndex: 9999,
  textDecoration: 'none',
  background: 'rgba(255, 255, 255, 0.6)',
  padding: '2px 6px',
  borderRadius: '3px',
  pointerEvents: 'auto',
  userSelect: 'text',
};

export default function BuildStamp() {
  const label = `build ${SHORT_SHA} \u00B7 ${FRIENDLY_TIME}`;
  const title = `commit ${SHA}\nbuilt ${BUILD_TIME}`;

  if (SHA === 'dev') {
    return (
      <span style={STAMP_STYLE} title={title} data-testid="build-stamp">
        {label}
      </span>
    );
  }

  return (
    <a
      href={`${REPO_URL}/commit/${SHA}`}
      target="_blank"
      rel="noopener noreferrer"
      style={STAMP_STYLE}
      title={title}
      data-testid="build-stamp"
    >
      {label}
    </a>
  );
}
